import axios from 'axios';
import { wrapAxiosInstance } from '../cache/modules/axiosFactory';
import { setupAuthInterceptor, retryWithBackoff } from '../common';
import { NAMESPACES } from '../configuration';
const BASE_URL = NAMESPACES.management_api.reservations_configuration;


// Helper function to configure the Axios instance
const api = setupAuthInterceptor(axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
}));

// Cached API wrapper for this namespace/group
const cachedApi = wrapAxiosInstance(api, {
  namespace: 'management_api',
  group: 'reservations_configuration',
  defaultTtl: '5m',
  hashLargeSegments: true,
  emitMeta: true,
});

export interface TableDetails {
  active: boolean;
  number: number;
}

export interface SalonReservation {
  active: boolean;
  name: string;
  tables: { [tableId: number]: TableDetails };
}

export interface Configuration {
  /** 1 = Mon … 7 = Sun → array of [start,end] 15-min slots */
  reservation_days: { [day: number]: [number, number][] };

  required_approval: boolean;
  confirmation_required: boolean;

  /** hours before service (nullable) */
  reservation_period: number | null;
  confirmation_period: number | null;

  /** layout for each day → salon → tables */
  salon_reservations: {
    [day: number]: { [salonId: number]: SalonReservation };
  };
}


/* ── Init / Get / Delete ───────────────────────────────────────────── */

export const initLocalReservationConfiguration = async (localId: number) => {
  const res = await retryWithBackoff(() =>
    api.post(`/InitLocalReservationConfiguration/${localId}`)
  );
  await cachedApi.clear();
  return res;
};

export const getLocalReservationConfigurationByLocalId = (localId: number) =>
  retryWithBackoff(() =>
    cachedApi.get(`/GetLocalReservationConfigurationByLocalId/${localId}`)
  );

export const deleteLocalReservationConfiguration = async (localId: number) => {
  const res = await retryWithBackoff(() =>
    api.delete(`/DeleteLocalReservationConfiguration/${localId}`)
  );
  await cachedApi.clear();
  return res;
};

/* ── Single-field updaters (one HTTP call per UI toggle) ───────────── */

export const updateRequiredApproval = async (localId: number, value: boolean) => {
  const res = await retryWithBackoff(() =>
    api.put(`/UpdateRequiredApproval/${localId}`, { value })
  );
  await cachedApi.clear();
  return res;
};

export const updateConfirmationRequired = async (localId: number, value: boolean) => {
  const res = await retryWithBackoff(() =>
    api.put(`/UpdateConfirmationRequired/${localId}`, { value })
  );
  await cachedApi.clear();
  return res;
};

export const updateReservationPeriod = async (
  localId: number,
  hours: number | null
) => {
  const res = await retryWithBackoff(() =>
    api.put(`/UpdateReservationPeriod/${localId}`, { value: hours })
  );
  await cachedApi.clear();
  return res;
};

export const updateConfirmationPeriod = async (
  localId: number,
  hours: number | null
) => {
  const res = await retryWithBackoff(() =>
    api.put(`/UpdateConfirmationPeriod/${localId}`, { value: hours })
  );
  await cachedApi.clear();
  return res;
};

export const updateReservationDays = async (
  localId: number,
  reservationDays: Configuration["reservation_days"]
) => {
  const res = await retryWithBackoff(() =>
    api.put(`/UpdateReservationDays/${localId}`, { reservation_days: reservationDays })
  );
  await cachedApi.clear();
  return res;
};

export const updateSalonReservations = async (
  localId: number,
  salonReservations: Configuration["salon_reservations"]
) => {
  const res = await retryWithBackoff(() =>
    api.put(`/UpdateSalonReservations/${localId}`, {
      salon_reservations: salonReservations,
    })
  );
  await cachedApi.clear();
  return res;
};

/* ── Generic PATCH (power-user) ───────────────────────────────────────
   `updates` is an array like:
     [{ path: ['reservation_period'], value: 12 },
      { path: ['reservation_days','1',0], value: [0,95] }]
*/
export interface PatchUpdate {
  path: (string | number)[]; 
  value: any;
}

export const patchReservationConfiguration = async (
  localId: number,
  updates: PatchUpdate[]
) => {
  const res = await retryWithBackoff(() =>
    api.put(`/PatchLocalReservationConfiguration/${localId}`, { updates })
  );
  await cachedApi.clear();
  return res;
};